import { concat, get } from '@ember/helper';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { computed } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { eq } from 'ember-truth-helpers';
import { camelize } from 'frontend-utils/utils/convert-case';
import type { CellTooltipBarValue } from 'lumen/components/hotel-and-market/revenue-insight/tables/cells/cell-tooltip-bar';
import HotelAndMarketRevenueInsightTablesCellsCellTooltipBar from 'lumen/components/hotel-and-market/revenue-insight/tables/cells/cell-tooltip-bar';
import type { TooltipBarGraphMultipleValue } from 'lumen/components/hotel-and-market/revenue-insight/tooltips/tooltip-bar-graph-multiple';
import {
  type CalculatedProperty,
  type Kpi,
  type KpiCategory,
  KPIS,
  PROGRESS_BAR_COLOUR_MAPPING,
  REVENUE_INSIGHT_SUBTITLE_TRANSLATION_KEYS,
  RI_SECTIONS,
  type RiSection,
  VALUE_CLASS_MAPPING,
} from 'lumen/utils/constants/ri-constant-values';

interface HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRowSignature {
  Args: {
    isChange: boolean;
    kpiCategory: KpiCategory;
    kpis: Kpi[];
    metrics: CalculatedProperty[];
    section: RiSection;
    tooltipValues: { max: Record<string, number> } & Record<
      CalculatedProperty,
      Record<string, number>
    >;
    valueMetrics?: CalculatedProperty[];
  };
}

export default class HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRow extends Component<HotelAndMarketRevenueInsightTablesRowsTooltipRevenueInsightRowSignature> {
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get progressBarValues() {
    const values: {
      [key in Kpi]?: TooltipBarGraphMultipleValue[];
    } = {};
    // Here we iterate per kpi: ['adr', 'nrOfRooms', 'revenue']
    this.args.kpis.forEach((kpi) => {
      values[kpi] = [];

      let { metrics } = this.args;

      if (kpi === KPIS.ADR) {
        metrics = metrics.filter((metric) => !metric.includes('cancellation'));
      }

      if (metrics.length === 1 && metrics[0] === 'forecastReference') {
        metrics = ['BOB', 'pickupLastYear'];
      }
      // Here we iterate per tooltipValueKey: [adr, 'nrOfRooms', 'revenue']
      metrics.forEach((metric) => {
        // If there is no value for the given kpi (short key), use kpi_kpiCategory (long key) as key for value and maxValue.
        // In some cases (like KpiListItem) we still use the long keys.
        const keyToUse =
          this.args.tooltipValues?.[metric]?.[kpi] !== null &&
          this.args.tooltipValues?.[metric]?.[kpi] !== undefined
            ? kpi
            : camelize(`${kpi}_${this.args.kpiCategory}`);

        const value = this.args.tooltipValues?.[metric]?.[keyToUse];
        const maxValue = this.args.tooltipValues?.max?.[keyToUse];

        if (value === undefined || maxValue === undefined) {
          return;
        }

        values[kpi]?.push({
          value,
          maxValue,
          backgroundColour:
            PROGRESS_BAR_COLOUR_MAPPING[
              metric as keyof typeof PROGRESS_BAR_COLOUR_MAPPING
            ],
          isStriped: metric.includes('cancellation'),
        });
      });
    });

    return values as Record<Kpi, TooltipBarGraphMultipleValue[]>;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subtitles() {
    const subtitles: { class: string | undefined; text: string }[] = [];

    if (this.valueMetrics.some((metric) => metric.includes('cancellation'))) {
      this.valueMetrics.forEach((metric) =>
        subtitles.push({
          text: this.intl.t(
            REVENUE_INSIGHT_SUBTITLE_TRANSLATION_KEYS[
              metric as keyof typeof REVENUE_INSIGHT_SUBTITLE_TRANSLATION_KEYS
            ] ?? metric,
          ),
          class:
            VALUE_CLASS_MAPPING[metric as keyof typeof VALUE_CLASS_MAPPING],
        }),
      );
    }

    return subtitles;
  }

  get titleText(): string {
    const primaryMetrics = this.args.metrics[0];

    const ignoreNetComparison = this.args.section === RI_SECTIONS.PICKUP;

    switch (primaryMetrics) {
      case 'BOB':
        return this.riSelections.currentPeriodText;
      case 'reference':
        return this.riSelections.getReferencePeriodText(ignoreNetComparison);
      case 'budget':
        return this.riSelections.budgetText;
      case 'budgetChange':
        return this.intl.t('revenue_insight.diff_budget.short');
      case 'forecast':
        return this.riSelections.forecastText;
      case 'forecastChange':
        return this.intl.t('revenue_insight.diff_forecast.short');
      case 'pickupLastYear':
        return this.riSelections.leftToPickupText;
      case 'forecastReference':
        return `${this.riSelections?.currentPeriodText} + ${this.riSelections?.leftToPickupText}`;
      default:
        return this.intl.t('general.not_available_short');
    }
  }

  /**
   * In some cases we display different values in the progress bars then the values.
   * If we pass an override then use that, otherwise just stick to the regular metrics
   */
  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get valueMetrics() {
    return this.args.valueMetrics ? this.args.valueMetrics : this.args.metrics;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'args.tooltipValues.max',
    'args.{isChange,kpiCategory,kpis}',
    'valueMetrics',
  )
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get values() {
    const values: { [key in Kpi]?: CellTooltipBarValue[] } = {};

    // Here we iterate per kpi: ['adr', 'nrOfRooms', 'revenue']
    this.args.kpis.forEach((kpi) => {
      values[kpi] = [];
      this.valueMetrics.forEach((metric) => {
        let valueClass =
          VALUE_CLASS_MAPPING[metric as keyof typeof VALUE_CLASS_MAPPING];

        // If there is no value for the given kpi (short key), use kpi_kpiCategory (long key) as key for value and maxValue.
        // In some cases (like KpiListItem) we still use the long keys.
        const keyToUse =
          this.args.tooltipValues?.[metric]?.[kpi] !== null &&
          this.args.tooltipValues?.[metric]?.[kpi] !== undefined
            ? kpi
            : camelize(`${kpi}_${this.args.kpiCategory}`);

        const value = this.args.tooltipValues?.[metric]?.[keyToUse];
        const maxValue = this.args.tooltipValues?.max?.[keyToUse];

        if (value === undefined || maxValue === undefined) {
          return;
        }

        if (this.args.isChange) {
          valueClass =
            value >= 0
              ? VALUE_CLASS_MAPPING.positive
              : VALUE_CLASS_MAPPING.negative;
        }

        values[kpi]?.push({
          value,
          maxValue,
          valueClass,
        });
      });
    });

    return values as Record<Kpi, CellTooltipBarValue[]>;
  }

  <template>
    <td class="tooltip-revenue-insight__row-title">
      {{this.titleText}}
    </td>
    {{#if this.subtitles.length}}
      <td class="tooltip-revenue-insight__row-title__subtitles no-border">
        <div class="flex flex-col">
          {{#each this.subtitles as |subtitle|}}
            <div class={{subtitle.class}}>
              {{subtitle.text}}
            </div>
          {{/each}}
        </div>
      </td>
    {{else}}
      <td class="no-border"></td>
    {{/if}}
    {{#each @kpis as |kpi|}}
      <HotelAndMarketRevenueInsightTablesCellsCellTooltipBar
        @isChange={{@isChange}}
        @isSingleKpi={{eq @kpis.length 1}}
        @progressBarValues={{get this.progressBarValues kpi}}
        @showPercentage={{eq kpi KPIS.OCCUPANCY}}
        @values={{get this.values kpi}}
        data-test-id={{concat "tooltip-bar-" kpi}}
      />
    {{/each}}
  </template>
}
